// ─── The saved-filter icon catalog, as Lucide components (f-02064) ─
// A STATIC, typed import map over the 96 names of FILTER_ICON_CATEGORIES in
// @pcp/shared (packages/shared/src/filters/icons.ts). Typed as
// Record<FilterIconName, LucideIcon>, so tsc fails on a missing, extra or
// misspelt name — that is the build gate for the catalog (the API image has no
// lucide-react to test against). Static named imports keep tree-shaking; never
// swap this for lucide's DynamicIcon.
//
// Only USER-CHOSEN saved-filter icons render through here (via <CatalogIcon> in
// lib/icons.tsx). Bar chrome — search, sort, plus, grip, ✕ — and quick-filter
// field icons use the house <Icon> semantic names, so a bar never mixes icon
// families on the Phosphor / Tabler / Heroicons palettes.

import {
  CircleDot, CircleCheck, CircleX, CirclePause, CircleDashed, CircleEllipsis, Hourglass, RotateCcw, Archive, Inbox, CheckCheck, ListChecks,
  User, Users, UserCheck, UserX, UserPlus, UserRoundSearch, Contact, IdCard, Handshake, Crown, ShieldUser, Headset,
  Clock, AlarmClock, Calendar, CalendarDays, CalendarClock, CalendarCheck, CalendarX, CalendarRange, History, Timer, Sunrise, Moon,
  Mail, MailOpen, MessageSquare, MessagesSquare, MessageCircle, Reply, Forward, Phone, Send, AtSign, Megaphone, Bell,
  Flag, TriangleAlert, OctagonAlert, CircleAlert, Siren, Flame, Zap, ShieldAlert, BellRing, Star, Bookmark, ThumbsDown,
  Ticket, Bug, Wrench, Hammer, Package, FileText, Paperclip, ClipboardList, NotebookPen, Folder, Layers, Tag,
  Building, Building2, Store, Landmark, Globe, House, Server, Database, Cloud, Monitor, Smartphone, MapPin,
  Funnel, Telescope, Lightbulb, Heart, Circle, Square, Triangle, Hexagon, Diamond, Hash, Eye, Target,
  type LucideIcon,
} from 'lucide-react';
import type { FilterIconName } from '@pcp/shared';

export const FILTER_ICON_COMPONENTS: Record<FilterIconName, LucideIcon> = {
  // Status & workflow
  'circle-dot': CircleDot, 'circle-check': CircleCheck, 'circle-x': CircleX, 'circle-pause': CirclePause,
  'circle-dashed': CircleDashed, 'circle-ellipsis': CircleEllipsis, hourglass: Hourglass, 'rotate-ccw': RotateCcw,
  archive: Archive, inbox: Inbox, 'check-check': CheckCheck, 'list-checks': ListChecks,
  // People & ownership
  user: User, users: Users, 'user-check': UserCheck, 'user-x': UserX, 'user-plus': UserPlus,
  'user-round-search': UserRoundSearch, contact: Contact, 'id-card': IdCard, handshake: Handshake, crown: Crown,
  'shield-user': ShieldUser, headset: Headset,
  // Time & dates
  clock: Clock, 'alarm-clock': AlarmClock, calendar: Calendar, 'calendar-days': CalendarDays,
  'calendar-clock': CalendarClock, 'calendar-check': CalendarCheck, 'calendar-x': CalendarX,
  'calendar-range': CalendarRange, history: History, timer: Timer, sunrise: Sunrise, moon: Moon,
  // Communication
  mail: Mail, 'mail-open': MailOpen, 'message-square': MessageSquare, 'messages-square': MessagesSquare,
  'message-circle': MessageCircle, reply: Reply, forward: Forward, phone: Phone, send: Send, 'at-sign': AtSign,
  megaphone: Megaphone, bell: Bell,
  // Priority & alerts
  flag: Flag, 'triangle-alert': TriangleAlert, 'octagon-alert': OctagonAlert, 'circle-alert': CircleAlert,
  siren: Siren, flame: Flame, zap: Zap, 'shield-alert': ShieldAlert, 'bell-ring': BellRing, star: Star,
  bookmark: Bookmark, 'thumbs-down': ThumbsDown,
  // Work & objects
  ticket: Ticket, bug: Bug, wrench: Wrench, hammer: Hammer, package: Package, 'file-text': FileText,
  paperclip: Paperclip, 'clipboard-list': ClipboardList, 'notebook-pen': NotebookPen, folder: Folder,
  layers: Layers, tag: Tag,
  // Places & systems
  building: Building, 'building-2': Building2, store: Store, landmark: Landmark, globe: Globe, house: House,
  server: Server, database: Database, cloud: Cloud, monitor: Monitor, smartphone: Smartphone, 'map-pin': MapPin,
  // Symbols & filters
  funnel: Funnel, telescope: Telescope, lightbulb: Lightbulb, heart: Heart, circle: Circle, square: Square,
  triangle: Triangle, hexagon: Hexagon, diamond: Diamond, hash: Hash, eye: Eye, target: Target,
};
